import { Form, Formik, type FormikConfig } from 'formik';
import { object, string } from 'yup';

import { type Library } from '@air/api/types';
import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { type Color } from '@air/primitive-color-picker';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { Textarea } from '@air/primitive-textarea';

import { getLibraryTitleValidation } from '~/components/LibraryBeta/LibraryPage/utils/libraryTitleValidationSchema';
import {
  LibraryPrivacySettings,
  type LibraryPrivacySettingsValue,
} from '~/components/LibraryBeta/LibraryPrivacySettings/LibraryPrivacySettings';
import { LibraryDetailsAccessibilityPrivate } from '~/components/LibraryBeta/LibrarySettingsModal/components/LibraryDetails/components/LibraryDetailsAccessibilityPrivate';
import { LibraryDetailsAccessibilityPublic } from '~/components/LibraryBeta/LibrarySettingsModal/components/LibraryDetails/components/LibraryDetailsAccessibilityPublic';
import { UpdateLibraryIcon } from '~/components/LibraryBeta/LibrarySettingsModal/components/UpdateLibraryIcon';

export const UpdateLibraryDetailsValidationSchema = object().shape({
  description: string(),
  privacy: string().oneOf(['invite-only', 'public-to-workspace', 'request-to-join']).required(),
  title: getLibraryTitleValidation({ requiredText: 'Library name is required.' }),
});

export type UpdateLibraryDetailsValues = {
  color?: Color;
  description?: string;
  icon?: string;
  privacy: LibraryPrivacySettingsValue;
  title: string;
};

export type UpdateLibraryDetailsProps = Required<
  Pick<FormikConfig<UpdateLibraryDetailsValues>, 'initialValues' | 'onSubmit'>
> & {
  canManageAccess?: boolean;
  accessibility?: Library['accessibility'];
  colors?: Color[];
  isDisabled?: boolean;
  isSubmitting?: boolean;
};

export const UpdateLibraryDetails = ({
  colors,
  initialValues,
  isDisabled,
  isSubmitting,
  onSubmit,
  canManageAccess,
  accessibility,
}: UpdateLibraryDetailsProps) => {
  return (
    <Formik<UpdateLibraryDetailsValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={UpdateLibraryDetailsValidationSchema}
    >
      {({ dirty, isValid }) => (
        <Form data-testid="UPDATE_LIBRARY_DETAILS_FORM">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="update-library-details-title" size="small">
                Name
              </Label>
              <div className="flex items-center gap-2">
                <UpdateLibraryIcon className="shrink-0" colors={colors} />
                <FormikField
                  isLabelHidden={true}
                  className="w-full"
                  component={<Input disabled={isDisabled} />}
                  id="update-library-details-title"
                  label="Title"
                  name="title"
                />
              </div>
            </div>

            <FormikField
              id="create-library-form-description"
              label="Description"
              name="description"
              component={<Textarea placeholder="What is this library used for" />}
            />

            {canManageAccess ? (
              <LibraryPrivacySettings name="privacy" />
            ) : (
              <div className="flex flex-col gap-1">
                <div className="font-semibold ads:text-metadata text-12 text-gray-10">Privacy</div>

                {accessibility === 'private' ? (
                  <LibraryDetailsAccessibilityPrivate />
                ) : (
                  <LibraryDetailsAccessibilityPublic />
                )}
              </div>
            )}
          </div>

          <div className="-mx-8 mt-8 -mb-7 flex items-center justify-end border-t ads:border-t-ads-gray-5 border-t-gray-5 px-8 py-4">
            <Button
              appearance="filled"
              disabled={!dirty || isDisabled || isSubmitting || !isValid}
              color="blue"
              isLoading={isSubmitting}
              size="large"
              type="submit"
            >
              Update library
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
